import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  InputLabel,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { getColors, shadows } from "./bookings.styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SaveIcon from "@mui/icons-material/Save";
import dayjs from "dayjs";

const BOOKING_TYPES = [
  { value: "test2", label: "Consultancy" },
  { value: "treatment", label: "Treatment" },
];

const EditBookingModal = ({ open, onClose, onSave, booking }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  // Form state
  const [bookingType, setBookingType] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [duration, setDuration] = useState(60);
  const [consultant, setConsultant] = useState("");
  const [internalComments, setInternalComments] = useState("");

  // Initialize form when booking changes
  useEffect(() => {
    if (booking) {
      setBookingType(booking.type || "appointment");
      setStatus(booking.status || "Scheduled");
      setDate(
        booking.date
          ? dayjs(booking.date).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD")
      );
      setSlot(booking.slot || "");
      setDuration(booking.duration || 60);
      setConsultant(booking.consultant?._id || "");
      setInternalComments(booking.internal_comments || "");
    }
  }, [booking]);

  const timeSlots = [
    "09:00 – 10:00",
    "10:00 – 11:00",
    "11:00 – 12:00",
    "12:00 – 13:00",
    "13:00 – 14:00",
    "14:00 – 15:00",
    "15:00 – 16:00",
    "16:00 – 17:00",
    "17:00 – 18:00",
  ];

  const durations = [
    { value: 30, label: "30 min" },
    { value: 60, label: "1 hour" },
    { value: 90, label: "1.5 hours" },
    { value: 120, label: "2 hours" },
  ];

  const statuses = [
    { value: "Scheduled", label: "SCHEDULED" },
    { value: "Confirmed", label: "CONFIRMED" },
    { value: "Ongoing", label: "ONGOING" },
    { value: "Completed", label: "COMPLETED" },
    { value: "Cancelled", label: "CANCELLED" },
  ];

  const handleSave = () => {
    onSave?.({
      type: bookingType,
      status,
      date,
      slot,
      duration,
      consultant,
      internal_comments: internalComments,
    });
    onClose();
  };

  const sectionLabelSx = {
    fontSize: 10,
    fontWeight: 700,
    color: colors.textMuted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    mb: 1.5,
  };

  const selectSx = {
    borderRadius: 2,
    fontSize: 13,
    bgcolor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor,
      borderWidth: 1,
    },
  };

  const getStatusChipStyle = (statusValue, isSelected) => {
    const baseStyle = {
      height: 32,
      fontSize: 11,
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderRadius: 2,
      "& .MuiChip-label": {
        px: 2,
      },
    };

    // Define status-specific colors
    const statusColors = {
      Scheduled: { bg: "#EFF6FF", color: "#3B82F6" },
      Confirmed: { bg: alpha(primaryColor, 0.1), color: primaryColor },
      Ongoing: { bg: "#F5F3FF", color: "#8B5CF6" },
      Completed: { bg: "#ECFDF5", color: "#10B981" },
      Cancelled: { bg: "#FEF2F2", color: "#EF4444" },
    };

    if (isSelected) {
      const selectedColor = statusColors[statusValue] || {
        bg: alpha(primaryColor, 0.1),
        color: primaryColor,
      };
      return {
        ...baseStyle,
        bgcolor: selectedColor.bg,
        color: selectedColor.color,
        border: "none",
        "&:hover": {
          bgcolor: selectedColor.bg,
          opacity: 0.9,
        },
      };
    }

    return {
      ...baseStyle,
      bgcolor: "#FFFFFF",
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      "&:hover": {
        bgcolor: colors.bgHover,
        borderColor: primaryColor,
      },
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.16)",
            maxWidth: 520,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${colors.borderLight}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  mb: 0.5,
                }}
              >
                Edit Booking - #{booking?._id?.slice(-8).toUpperCase() || "BOOKING"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                {booking?.clientName || "Client"} • Update appointment details
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: colors.textMuted,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Booking Details Section */}
          <Typography sx={sectionLabelSx}>BOOKING DETAILS</Typography>
          <Stack spacing={2.5} mb={1}>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: colors.textSecondary,
                    mb: 1,
                  }}
                >
                  Booking Type
                </Typography>
                <Select
                  value={bookingType}
                  onChange={(e) => setBookingType(e.target.value)}
                  sx={{ ...selectSx, height: 44 }}
                >
                  {BOOKING_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      <Typography
                        sx={{ fontSize: 13, color: colors.textPrimary }}
                      >
                        {t.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: colors.textSecondary,
                    mb: 1,
                  }}
                >
                  Duration
                </Typography>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  sx={{ ...selectSx, height: 44 }}
                >
                  {durations.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      <Typography
                        sx={{ fontSize: 13, color: colors.textPrimary }}
                      >
                        {d.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Booking Status */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textSecondary,
                  mb: 1.5,
                }}
              >
                Booking Status
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {statuses.map((s) => (
                  <Chip
                    key={s.value}
                    label={s.label}
                    onClick={() => setStatus(s.value)}
                    sx={getStatusChipStyle(s.value, status === s.value)}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>

          {/* Schedule Section */}
          <Typography sx={sectionLabelSx}>SCHEDULE</Typography>
          <Stack direction="row" spacing={2} mb={3}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textSecondary,
                  mb: 1,
                }}
              >
                Date
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayOutlinedIcon
                      sx={{ fontSize: 18, color: colors.textMuted, mr: 1 }}
                    />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    bgcolor: "#fff",
                    height: 44,
                    "& fieldset": {
                      borderColor: colors.border,
                    },
                    "&:hover fieldset": {
                      borderColor: primaryColor,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: primaryColor,
                      borderWidth: 1,
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textSecondary,
                  mb: 1,
                }}
              >
                Time Slot
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  displayEmpty
                  sx={{ ...selectSx, height: 44 }}
                >
                  <MenuItem value="">
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                      Select slot
                    </Typography>
                  </MenuItem>
                  {timeSlots.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Typography
                        sx={{ fontSize: 13, color: colors.textPrimary }}
                      >
                        {t}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {/* Notes Section */}
          <Typography sx={sectionLabelSx}>NOTES</Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.textSecondary,
              mb: 1,
            }}
          >
            Internal Comments
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Enter any internal notes or instructions..."
            value={internalComments}
            onChange={(e) => setInternalComments(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: "#fff",
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
            }}
          />

          {/* Actions */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ borderTop: `1px solid ${colors.borderLight}`, pt: 3 }}
          >
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                color: colors.textSecondary,
                px: 3,
                py: 1,
                "&:hover": {
                  bgcolor: "transparent",
                  color: colors.textPrimary,
                },
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                bgcolor: primaryColor,
                color: "#FFF",
                px: 3,
                py: 1.25,
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: primaryColor,
                  opacity: 0.9,
                  boxShadow: "none",
                },
              }}
            >
              Save Changes
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditBookingModal;
